import { GET_OPPORTUNITY_RESOURCE_DATA } from "@/services/constants";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getOpportunitiesResource } from "./apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { ResourceType } from "@/pages/explorer/types";
import { useInsightState } from "@/modules/insights/context";

export const useDateFromSearchParams = (
  initialStartDate?: Date,
  initialEndDate?: Date,
  prefix = ""
) => {
  const getKey = (key: string) => (prefix ? `${prefix}_${key}` : key);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [startDate, setStartDate] = useState(() => {
    const value = searchParams.get(getKey("start_date"));
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return initialStartDate || dayjs().subtract(28, "days").toDate();
  });

  const [endDate, setEndDate] = useState(() => {
    const value = searchParams.get(getKey("end_date"));
    if (value) {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return initialEndDate || dayjs().toDate();
  });

  useEffect(() => {
    const searchParamStartDate = searchParams.get(getKey("start_date"));
    const searchParamEndDate = searchParams.get(getKey("end_date"));

    if (searchParamStartDate) {
      const parsedStartDate = new Date(searchParamStartDate);
      // Skip invalid dates from malformed URL params
      if (!isNaN(parsedStartDate.getTime())) {
        // Avoid redundant state updates (and duplicate queries) when URL dates haven't changed
        setStartDate(prev =>
          prev.valueOf() === parsedStartDate.valueOf() ? prev : parsedStartDate
        );
      }
    }

    if (searchParamEndDate) {
      const parsedEndDate = new Date(searchParamEndDate);
      // Skip invalid dates from malformed URL params
      if (!isNaN(parsedEndDate.getTime())) {
        // Avoid redundant state updates (and duplicate queries) when URL dates haven't changed
        setEndDate(prev => (prev.valueOf() === parsedEndDate.valueOf() ? prev : parsedEndDate));
      }
    }
  }, [searchParams]);

  const onUpdate = useCallback(
    (newStartDate: Date, newEndDate: Date) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          params.set(getKey("start_date"), newStartDate?.toISOString());
          params.set(getKey("end_date"), newEndDate?.toISOString());
          return params.toString();
        },
        { replace: true }
      );
    },
    [setSearchParams, prefix]
  );

  const navigateTo = (path: string, state?: unknown) =>
    navigate(
      `${path}?${getKey("start_date")}=${startDate?.toISOString()}&${getKey(
        "end_date"
      )}=${endDate?.toISOString()}`,
      { state }
    );

  return { startDate, endDate, onUpdate, navigateTo };
};

const EMPTY_OPPORTUNITIES = { data: [] };

export const useResourceOpportunities = ({
  resourceUri,
  resourceType,
  instanceId,
  startDate: startDateProp,
  endDate: endDateProp,
}: {
  resourceUri?: string;
  resourceType: ResourceType;
  instanceId?: string;
  /** Dates to fetch with; defaults to the unprefixed URL date range. */
  startDate?: Date;
  endDate?: Date;
}) => {
  const fallbackDates = useDateFromSearchParams();
  const startDate = startDateProp ?? fallbackDates.startDate;
  const endDate = endDateProp ?? fallbackDates.endDate;
  const {
    selectedTab: status,
    sort: { sortBy, sortOrder },
  } = useInsightState();

  return useQuery({
    queryKey: [
      GET_OPPORTUNITY_RESOURCE_DATA,
      resourceUri,
      resourceType,
      startDate,
      endDate,
      status,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      getOpportunitiesResource({
        resource_uri: resourceUri!,
        resource_type: resourceType,
        instance_id: instanceId || "",
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
        ...(status && { status }),
        ...(sortBy && { sort: sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
      }),
    placeholderData: EMPTY_OPPORTUNITIES,
    enabled: Boolean(resourceUri),
  });
};
